// En qué paso del panel vive la CONFIGURACIÓN de cada modo — el único lugar donde eso
// se decide (ADR-2026-09-10-el-paso-de-cada-modo-se-decide-en-un-solo-lugar).
//
// `panelStep` nació con el flujo de DOS pasos: elegir el producto (`choose-product`) y
// después configurar (`configure`). Por eso "abrir un modo ya configurado" se escribió
// como `setPanelStep('configure')` literal, en tres lugares distintos. El rediseño rompió
// esa equivalencia: hay modos que viven ENTEROS en `choose-product` y para los que
// `configure` es la pantalla vieja que quedó montada en el shell. Mandarlos ahí no es un
// paso raro: es mostrarles al usuario una interfaz que ya no es la del modo.
//
// Este módulo existe para que esa pregunta tenga UNA respuesta. Quien necesite saber "a
// qué paso abro este modo con la configuración puesta" la pide acá, no la escribe.

import { esCatalogoIA, esDesignModelo } from './modesCatalog';

/** Los pasos que puede tomar `panelStep` en `InterfazV2`. */
export type PasoDelPanel =
  | 'preview'
  | 'choose-product'
  | 'pick-product'
  | 'upload-product'
  | 'select-vistas'
  | 'configure'
  | 'asistente'
  | 'prompt-libre'
  | 'creador-video';

/**
 * Modos que son UNA SOLA PANTALLA: no tienen paso 2, así que su configuración vive en
 * `choose-product` de punta a punta y `configure` es la pantalla anterior al rediseño.
 *
 *   • `design-modelo` — ADR-2026-08-21-modelo-v2-una-sola-columna
 *   • `design-auto`   — ADR-2026-08-21-automatico-con-el-diseno-nuevo
 *
 * Si mañana otro modo se pasa al rediseño, se suma acá y deja de poder caer en la
 * pantalla vieja por cualquiera de los tres caminos que abren un modo configurado.
 */
export const MODOS_DE_UNA_SOLA_PANTALLA = ['design-modelo', 'design-auto'] as const;

/** Modos que tienen su propio sheet en vez del panel de pasos. */
const PASO_PROPIO: Record<string, PasoDelPanel> = {
  asistente: 'asistente',
  'prompt-libre': 'prompt-libre',
  'creador-video': 'creador-video',
};

/**
 * A qué paso se abre un modo que YA tiene su configuración resuelta: el restore del
 * "Atrás" desde los resultados, el modo que se abre con el producto puesto desde afuera
 * y el "Confirmar" del selector de productos.
 *
 * `infografia` cae en `configure` a propósito aunque comparta flujo con el Catálogo: no
 * tiene picker de vistas —su única vista se presetea— y su `configure` ES el review.
 */
export function pasoDeConfiguracion(kind?: string | null): PasoDelPanel {
  if (kind && PASO_PROPIO[kind]) return PASO_PROPIO[kind];
  if (esDesignModelo(kind) || kind === 'design-auto') return 'choose-product';
  if (esCatalogoIA(kind) || kind === 'catalogo-muebles') return 'select-vistas';
  return 'configure';
}

/** ¿Este modo se configura en su única pantalla, sin paso 2? */
export function esModoDeUnaSolaPantalla(kind?: string | null): boolean {
  return (MODOS_DE_UNA_SOLA_PANTALLA as readonly string[]).includes(kind ?? '');
}

/**
 * El paso al que se puede mover un modo, corregido: `configure` en un modo de una sola
 * pantalla se lee como lo que quiso decir — su pantalla.
 *
 * Es la red de contención que aplica `InterfazV2` a TODOS sus cambios de paso. El corte
 * no está en cada llamador porque mandar a `configure` un modo del rediseño no rompe
 * nada visible: dibuja el panel apilado anterior, que sigue montado en el shell para los
 * modos de dos pasos. Sin error ni pantalla en blanco, un camino nuevo que lo haga se
 * descubre recién cuando un usuario lo cuenta — ya pasó por tres caminos distintos.
 */
export function pasoSeguro(paso: PasoDelPanel, kind?: string | null): PasoDelPanel {
  return paso === 'configure' && esModoDeUnaSolaPantalla(kind)
    ? 'choose-product'
    : paso;
}
